/**
 * Common ReviewStars component used by other modules as a reusable component which returns the review stars as fullfilled and half filled.
 *
 * @module views/components/ReviewStars
 * @memberof -Common
 */
import './ReviewStars.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';
import { starIcons } from '@ulta/utils/starIcons/starIcons';

import * as utils from './ReviewStars';

/**
 * Represents a ReviewStars Component
 *
 * @method
 * @param {ReviewStarsProps} props - React properties passed from composition
 * @returns ReviewStars
*/
const ReviewStars = ( props ) => {

  const {
    max = ReviewStars.defaultProps.max,
    size = ReviewStars.defaultProps.size,
    rating = ReviewStars.defaultProps.rating,
    reviews = ReviewStars.defaultProps.reviews,
    reviewLink = ReviewStars.defaultProps.reviewLink,
    reviewsCountLabel,
    reviewable,
    ratingAction,
    writeReviewAction,
    qAndAAction,
    askQuestionAction,
    reviewAccessibilityLabel
  } = props;
  let stars = starIcons( { rating, max } );

  const reviewsAccessibilityLabel = carouselUtils.handleTokenizedString( reviewAccessibilityLabel, [rating, reviews] );

  const hasReviewsAndRating = stars.length > 0 && !!reviews;

  const reviewsCountString = reviews?.toLocaleString() || '0';

  const showJumpLink = ratingAction?.label && reviewable && !!reviews;

  const showWriteReviewLink = writeReviewAction?.label && reviewable && !reviews;

  /*
   * We disable A11y recommendations on the wrapper div
   * because we're just using for scrollToElement to maintain touch targets in PDP
   */
  return (
    <div className='ReviewStars'>
      { hasReviewsAndRating && reviewable &&

      <div className='ReviewStars__Content'
        tabIndex={ -1 }
        aria-hidden={ true }
        onClick={ ()=>{
          triggerDatacaptureEvent( ratingAction?.dataCaptureData );
          scrollToElement( { el: document.querySelector( '#reviews' ) } );
        } }
      >
        { utils.composeRating( { rating, size, stars } ) }
      </div>
      }
      { showJumpLink &&
        <>
          <Link compact
            url={ '#reviews' }
            disabled={ false }
            tabIndex={ 0 } /* added for ada purposes */
            ariaLabel={ reviewsAccessibilityLabel }
            onClick={ ()=> {
              scrollToElement( { el: document.querySelector( '#reviews' ) } );
            } }
            iconImage={ 'ArrowDown' }
            iconsize={ 's' }
            iconRight={ true }
            action={ ratingAction }
          >
            <Text
              htmlTag='span'
              textStyle='body-3'
              ariaHidden={ true }
            >
              { reviewsCountString } { reviewsCountLabel }
            </Text>
          </Link>
        </>
      }
      { hasReviewsAndRating && !reviewable &&
      <>
        { utils.composeRating( { rating, size, stars } ) }
        <Text
          htmlTag='span'
          textStyle='body-3'
          ariaHidden={ true }
        >
          { reviewsCountString } { reviewsCountLabel }
        </Text>
      </>
      }
      { showWriteReviewLink &&
      <Link compact
        url={ writeReviewAction?.url }
        action={ writeReviewAction }
      >
        <Text
          htmlTag='span'
          textStyle='body-3'
        >
          { writeReviewAction?.label }
        </Text>
      </Link>
      }
      { qAndAAction?.label &&
      <Link compact
        url={ '#productQA' }
        tabIndex={ 0 } /* added for ada purposes */
        disabled={ false }
        ariaLabel={ qAndAAction.label }
        onClick={ ()=>{
          scrollToElement( { el: document.querySelector( '#productQA' ) } );
          triggerDatacaptureEvent( qAndAAction.dataCaptureData );
        }
        }
        iconImage={ 'ArrowDown' }
        iconsize={ 's' }
        iconRight={ true }
        action={ qAndAAction }
      >
        <Text
          htmlTag='span'
          textStyle='body-3'
        >
          { qAndAAction.label }
        </Text>
      </Link>
      }
      { askQuestionAction &&
      <Link compact
        url={ askQuestionAction.url }
        action={ askQuestionAction }
      >
        <Text
          htmlTag='span'
          textStyle='body-3'
        >
          { askQuestionAction.label }
        </Text>
      </Link>
      }
    </div>

  );
};

/**
  *
  * @method triggerDatacaptureEvent
  * @summary This method triggers datacapture events
  * @param {Object} datacaptureEvent dxl response
  */
export const triggerDatacaptureEvent = ( datacaptureEvent ) =>{
  if( datacaptureEvent ){
    datacapture.processEvents( { dataCapture : datacaptureEvent }, datacaptureEvent.clientEvent?.toLowerCase() );
  }
};

/**
 * Represents a composeRating method which renders rating and stars
 *
 * @method
 * @param {object} data - data
 * @param {string} data.rating - 0 to 5 decimal number indicating the rating
 * @param {string} data.size - Size of the star icon (`s`, `m` or `l`)
 * @param {array} data.stars - Contains Array of Star Icon - Filled/Outlined
*/
export const composeRating = ( data ) => {
  const { rating, stars, size } = data || {};

  if( !stars ){
    return;
  }
  return (
    <>
      <Text
        htmlTag='span'
        textStyle='body-3'
        ariaHidden={ true }
      >
        { rating }
      </Text>
      { stars.map( ( i, ind ) => {
        return (
          <Icon
            aria-hidden={ true }
            key={ 'star' + ind }
            alt={ i }
            name={ i }
            size={ size }
          />
        );
      } )
      }
    </>
  );
};

/**
 * Property type definitions
 * @typedef ReviewStarsProps
 * @type {object}
 * @property {number} max - Maximum number of stars to display
 * @property {string} noReviewText - Text to indicate there are no reviews
 * @property {number} rating - 0 to 5 decimal number indicating the rating
 * @property {object} ratingAction - This holds the action on rating
 * @property {number} reviews - Number of total reviews
 * @property {string} reviewsCountLabel - reviews count label
 * @property {string} reviewLink - Link to reviews
 * @property {boolean} reviewable - Flag that indicates if a "write a review" link show display
 * @property {string} size - Size of the star icon (`s`, `m` or `l`).
 * @property {object} writeReviewAction - Text and link to indicate there are no reviews
 * @property {object} askQuestionAction - This holds the action on ask question
 * @property {object} qAndAAction - This holds the action for QandA
 * @property {string} reviewAccessibilityLabel - Sets review accessibility label
*/
ReviewStars.propTypes = {
  /** Maximum number of stars to display */
  max: PropTypes.number,
  /** Text to indicate there are no reviews */
  noReviewText: PropTypes.string,
  /** 0 to 5 decimal number indicating the rating */
  rating: PropTypes.number,
  /** Hold the rating action */
  ratingAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** Number of total reviews */
  reviews: PropTypes.number,
  /** reviews count label */
  reviewsCountLabel: PropTypes.string,
  /** Link to reviews */
  reviewLink: PropTypes.string,
  /** Flag that indicates if a "write a review" link show display */
  reviewable: PropTypes.bool,
  /** Size of the star icon (`s`, `m` or `l`). */
  size: PropTypes.oneOf( [`s`, `m`, `l`] ),
  /** Text and link to indicate there are no reviews */
  writeReviewAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This holds the action on ask question */
  askQuestionAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** Sets the QandA Action */
  qAndAAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** Sets review accessibility label */
  reviewAccessibilityLabel: PropTypes.string
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {number} max=5 - Default max value
 * @property {number} rating=0 - Default rating value
 * @property {number} reviews=0 - Default reviews value
 * @property {string} reviewLink='#' - Default reviewLink value
 * @property {boolean} reviewable='false' - Default reviewable value
 * @property {string} size='s' - Default size value
*/
ReviewStars.defaultProps = {
  max: 5,
  rating: 0,
  reviews: 0,
  reviewLink: '#',
  reviewable: false,
  size: 's'
};

export default ReviewStars;
